/**
 * Tipos para OBPHR2Service — Otros Bienes del Patrimonio Histórico de Andalucía
 *
 * Datos: OBPH / IAPH (Junta de Andalucía)
 * Incluye: ~8.305 bienes inmuebles NO catalogados en el CGPHA
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /obph/{ine5}.json.gz
 *
 * @module types/obph
 * @version 1.0.0
 * @date 2026-02-20
 */

// =============================================================================
// FEATURE — Esquema generado por obph-download.ts
// =============================================================================

/** Feature OBPH almacenada en R2 */
export interface OBPHFeature {
  /** ID OBPH interno */
  id: number;
  /** Código OBPH (numérico, ej: 180510009) */
  codigo: number;
  /** Nombre del bien patrimonial */
  nombre: string;
  /** Tokens pre-normalizados para búsqueda (lowercase, sin tildes) */
  tokens: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Código INE municipio (5 dígitos) */
  cod_ine: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Carácter del bien (ej: "Arqueológico", "Arquitectónico, Etnológico") */
  caracter: string;
  /** Tipología general (ej: "Edificios agropecuarios", "Yacimientos arqueológicos") */
  tipologia_gen: string;
  /** Tipología específica (ej: "Cortijos", "Almazaras, Molinos") */
  tipologia_esp: string;
  /** Actividad asociada (ej: "Actividad agropecuaria", "Molinería") */
  actividad: string;
}

// =============================================================================
// OPCIONES DE BÚSQUEDA
// =============================================================================

/** Opciones para buscar en OBPH R2 */
export interface OBPHSearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codINE: string;
  /** Nombre del bien a buscar */
  name: string;
  /** Confianza mínima para aceptar resultado (0-100) */
  minConfidence?: number;
  /** Máximo de resultados */
  maxResults?: number;
  /** Filtrar por carácter (ej: "Arqueológico") */
  caracter?: string;
}

// =============================================================================
// RESULTADO
// =============================================================================

/** Resultado de búsqueda OBPH */
export interface OBPHResult {
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Confianza del match (0-100) */
  confidence: number;
  /** Precisión en metros */
  precision: number;
  /** Fuente */
  source: 'obph_r2';
  /** Feature original */
  feature: OBPHFeature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES DE VALIDACIÓN
// =============================================================================

/** Rango X válido para coordenadas OBPH Andalucía (EPSG:25830) */
export const OBPH_X_RANGE: [number, number] = [100_000, 600_000];

/** Rango Y válido para coordenadas OBPH Andalucía (EPSG:25830) */
export const OBPH_Y_RANGE: [number, number] = [3_980_000, 4_290_000];

/** Precisión estimada en metros (coordenadas OBPH aproximadas) */
export const OBPH_PRECISION_M = 30;
